import { useState } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/button";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { cancelCaregiverInviteApi, resendCaregiverInviteApi } from "@/portals/patient/api/familyApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import type { CaregiverAccess } from "@/shared/types/family";
import { deriveNameFromEmail } from "../lib/familyUtils";
import { InviteCaregiverModal } from "./InviteCaregiverModal";
import { ManageCaregiverAccessModal } from "./ManageCaregiverAccessModal";
import { useCountdown } from "@/portals/patient/pages/auth/useCountdown";

// Mirrors the backend's own CAREGIVER_INVITE_RESEND_COOLDOWN_SECONDS (see
// FamilyService.resend_invite) — this is the optimistic, no-round-trip guard
// for the common case (button disables the instant a resend succeeds); the
// backend enforces the real limit regardless, and its retryAfterSeconds
// (e.g. after a page reload resets this local cooldown) is honored below.
const RESEND_COOLDOWN_SECONDS = 30;

interface ManageCaregiversSectionProps {
  caregivers: CaregiverAccess[];
  /** Refetches the caregivers list from the backend — called after any successful invite/resend/cancel. */
  onChange: () => void;
}

type CaregiverAccessStatusKey = CaregiverAccess["status"];

function getInitials(name: string): string {
  const parts = name.split(" ").filter(Boolean);
  return parts.slice(0, 2).map((p) => p.charAt(0)).join("").toUpperCase();
}

function formatDate(dateString: string): string {
  if (!dateString) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const STATUS_CONFIG: Record<CaregiverAccessStatusKey, { label: string; dot: string; pill: string }> = {
  active: { label: "Active", dot: "bg-[#16A34A]", pill: "bg-[#DCFCE7] text-[#16A34A]" },
  pending: { label: "Pending Invitation", dot: "bg-[#D97706]", pill: "bg-[#FEF3C7] text-[#B45309]" },
  expired: { label: "Expired", dot: "bg-[#9CA3AF]", pill: "bg-[#F3F4F6] text-[#6B7280]" },
  declined: { label: "Declined", dot: "bg-[#E4002B]", pill: "bg-[#FFF0F3] text-[#E4002B]" },
  revoked: { label: "Access Revoked", dot: "bg-[#9CA3AF]", pill: "bg-[#F3F4F6] text-[#6B7280]" },
};

interface CaregiverRowProps {
  caregiver: CaregiverAccess;
  isCancelling: boolean;
  isResending: boolean;
  onManageAccess: () => void;
  /** succeeded: starts the full local cooldown. retryAfterSeconds: present
   * when the backend itself rejected the attempt as too soon (e.g. this
   * row's local cooldown was reset by a page reload) — starts the cooldown
   * for however long the backend says is actually left, instead of just
   * showing an error and leaving the button clickable again. */
  onResend: () => Promise<{ succeeded: boolean; retryAfterSeconds?: number }>;
  onCancel: () => void;
}

function CaregiverRow({ caregiver, isCancelling, isResending, onManageAccess, onResend, onCancel }: CaregiverRowProps) {
  const status = STATUS_CONFIG[caregiver.status];
  // Purely a frontend guard against spamming the button — see
  // RESEND_COOLDOWN_SECONDS above. Starts at 0 (not counting) until a resend
  // actually succeeds (or the backend reports one is already in progress).
  const resendCooldown = useCountdown(0);
  const resendOnCooldown = resendCooldown.secondsLeft > 0;

  const handleResendClick = async () => {
    const { succeeded, retryAfterSeconds } = await onResend();
    if (succeeded) resendCooldown.reset(RESEND_COOLDOWN_SECONDS);
    else if (retryAfterSeconds) resendCooldown.reset(retryAfterSeconds);
  };
  // A pending invite has no linked account yet, so the backend has no real
  // name to give — fall back to deriving a display name from the email.
  const displayName = caregiver.name ?? deriveNameFromEmail(caregiver.email);

  return (
    <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-5">
      <div className="flex min-w-0 items-start gap-4">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xs font-bold !text-white",
            caregiver.status === "expired" ? "bg-[#9CA3AF]" : "bg-[#111827]"
          )}
        >
          {getInitials(displayName)}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-extrabold text-[#0A2947]">{displayName}</h3>
            <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold", status.pill)}>
              <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
              {status.label}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-[#6B7280]">{caregiver.email}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {caregiver.categories.map((category) => (
              <span key={category} className="rounded-full bg-[#FFF0F3] px-2.5 py-0.5 text-[11px] font-semibold text-[#E4002B]">
                {category}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-2">
        {caregiver.status === "active" && (
          <Button
            variant="outline"
            onClick={onManageAccess}
            className="!flex !items-center !gap-1.5 !rounded-lg !border-[#E5E7EB] !px-3.5 !py-2 !text-xs !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!text-[#0A2947]"
          >
            <Icon icon="lucide:settings" className="h-3.5 w-3.5" />
            Manage Access
          </Button>
        )}
        {caregiver.status === "revoked" && (
          <Button
            variant="outline"
            onClick={onManageAccess}
            className="!flex !items-center !gap-1.5 !rounded-lg !border-[#E5E7EB] !px-3.5 !py-2 !text-xs !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!text-[#0A2947]"
          >
            <Icon icon="lucide:unlock" className="h-3.5 w-3.5" />
            Give Access
          </Button>
        )}
        {caregiver.status === "pending" && (
          <>
            <Button
              variant="outline"
              onClick={handleResendClick}
              disabled={isResending || isCancelling || resendOnCooldown}
              title={resendOnCooldown ? "You can resend again shortly" : undefined}
              className={cn(
                "!flex !items-center !gap-1.5 !rounded-lg !border-[#E5E7EB] !px-3.5 !py-2 !text-xs !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!text-[#0A2947]",
                resendOnCooldown && "!cursor-not-allowed !opacity-50 hover:!border-[#E5E7EB]",
              )}
            >
              <Icon
                icon={isResending ? "lucide:loader-2" : resendOnCooldown ? "lucide:ban" : "lucide:send"}
                className={cn("h-3.5 w-3.5", isResending && "animate-spin")}
              />
              {isResending ? "Sending…" : "Resend"}
            </Button>
            <Button
              variant="link"
              onClick={onCancel}
              disabled={isCancelling || isResending}
              className="!flex !items-center !gap-1 !p-0 !text-xs !font-semibold !text-[#E4002B] hover:!underline"
            >
              <Icon icon={isCancelling ? "lucide:loader-2" : "lucide:x"} className={cn("h-3 w-3", isCancelling && "animate-spin")} />
              {isCancelling ? "Cancelling…" : "Cancel Invite"}
            </Button>
          </>
        )}
        {caregiver.status === "expired" && (
          <Button
            variant="outline"
            onClick={handleResendClick}
            disabled={isResending || resendOnCooldown}
            title={resendOnCooldown ? "You can resend again shortly" : undefined}
            className={cn(
              "!flex !items-center !gap-1.5 !rounded-lg !border-[#E5E7EB] !px-3.5 !py-2 !text-xs !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!text-[#0A2947]",
              resendOnCooldown && "!cursor-not-allowed !opacity-50 hover:!border-[#E5E7EB]",
            )}
          >
            <Icon
              icon={isResending ? "lucide:loader-2" : resendOnCooldown ? "lucide:ban" : "lucide:send"}
              className={cn("h-3.5 w-3.5", isResending && "animate-spin")}
            />
            {isResending ? "Sending…" : "Re-invite"}
          </Button>
        )}
        {caregiver.expiresDate && (
          <p className="text-[11px] text-[#9CA3AF]">Expires {formatDate(caregiver.expiresDate)}</p>
        )}
      </div>
    </div>
  );
}

export function ManageCaregiversSection({ caregivers, onChange }: ManageCaregiversSectionProps) {
  const { profile } = usePatientProfile();
  const [showInviteCaregiver, setShowInviteCaregiver] = useState(false);
  const [manageAccessCaregiverId, setManageAccessCaregiverId] = useState<string | null>(null);
  // Tracked separately from *which* caregiver, and from *which action* —
  // Resend and Cancel each show their own distinct in-progress state (see
  // CaregiverRow) rather than one shared "isBusy" that couldn't tell them apart.
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const manageAccessCaregiver = caregivers.find((c) => c.id === manageAccessCaregiverId) ?? null;

  const handleResend = async (caregiver: CaregiverAccess): Promise<{ succeeded: boolean; retryAfterSeconds?: number }> => {
    setResendingId(caregiver.id);
    const { error, retryAfterSeconds } = await resendCaregiverInviteApi(caregiver.id);
    setResendingId(null);
    if (error) {
      // A cooldown rejection doesn't need its own toast — the button itself
      // (disabled, blocked cursor) already communicates "not yet".
      if (!retryAfterSeconds) toastError(error);
      return { succeeded: false, retryAfterSeconds };
    }
    onChange();
    toastSuccess(`Invite resent to ${caregiver.name ?? caregiver.email}`);
    return { succeeded: true };
  };

  const handleCancel = async (caregiver: CaregiverAccess) => {
    setCancellingId(caregiver.id);
    const { error } = await cancelCaregiverInviteApi(caregiver.id);
    setCancellingId(null);
    if (error) {
      toastError(error);
      return;
    }
    onChange();
    toastSuccess("Invitation cancelled.");
  };

  const activeCount = caregivers.filter((c) => c.status === "active").length;
  const pendingCount = caregivers.filter((c) => c.status === "pending").length;
  const expiredCount = caregivers.filter((c) => c.status === "expired").length;
  const declinedCount = caregivers.filter((c) => c.status === "declined").length;
  const revokedCount = caregivers.filter((c) => c.status === "revoked").length;

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-extrabold text-[#0A2947]">People With Access To Your Records</h2>
        <Button
          onClick={() => setShowInviteCaregiver(true)}
          className="!flex !items-center !gap-2 !rounded-lg !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          <Icon icon="lucide:plus" className="h-4 w-4 text-white" />
          Invite Caregiver
        </Button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] px-5 py-3">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Access Summary</span>
          <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-[#6B7280]">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
              Active ({activeCount})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D97706]" />
              Pending ({pendingCount})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF]" />
              Expired ({expiredCount})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E4002B]" />
              Declined ({declinedCount})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF]" />
              Revoked ({revokedCount})
            </span>
          </div>
        </div>

        <div className="divide-y divide-[#E5E7EB]">
          {caregivers.map((caregiver) => (
            <CaregiverRow
              key={caregiver.id}
              caregiver={caregiver}
              isResending={resendingId === caregiver.id}
              isCancelling={cancellingId === caregiver.id}
              onManageAccess={() => setManageAccessCaregiverId(caregiver.id)}
              onResend={() => handleResend(caregiver)}
              onCancel={() => handleCancel(caregiver)}
            />
          ))}
        </div>

        {caregivers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-extrabold text-[#0A2947]">No caregivers have access yet</p>
            <p className="mt-1 text-xs font-semibold text-[#9CA3AF]">Invite someone to share access to your records</p>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#DBEAFE] bg-[#EFF6FF] p-4 text-xs leading-relaxed text-[#4B5563]">
        <Icon icon="lucide:info" className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
        <p>
          Caregivers can only view the record categories you explicitly share — they cannot edit, download in bulk, or access
          other sections of your account. You can revoke access at any time.
        </p>
      </div>

      <InviteCaregiverModal
        open={showInviteCaregiver}
        onOpenChange={setShowInviteCaregiver}
        onInvited={onChange}
        caregivers={caregivers}
      />
      {manageAccessCaregiver && (
        <ManageCaregiverAccessModal
          open={manageAccessCaregiverId !== null}
          onOpenChange={(open) => setManageAccessCaregiverId(open ? manageAccessCaregiverId : null)}
          caregiver={manageAccessCaregiver}
          patientName={`${profile.firstName} ${profile.lastName}`.trim()}
          onChange={onChange}
        />
      )}
    </div>
  );
}
